// Attribution Insights — dashboard widget showing the volume of
// speaker-vs-extension mismatches over time. Mounted on the Admin
// Overview page (master admin) so seat-swap trends are visible at a
// glance.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AlertTriangle, ArrowUpRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2.5 shadow-lg text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-slate-700" style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export const AttributionInsightsCard = ({ days = 30 }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/analytics/attribution-insights?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, days]);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-6" data-testid="attribution-insights-loading">
        <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { weekly_series = [], totals = {}, top_extensions = [] } = data;
  const anyActivity = totals.flagged > 0 || totals.manual_reassigned > 0 || totals.open_flags > 0;

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-5"
      data-testid="attribution-insights-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">Attribution Insights</h3>
          <span className="text-xs text-white/40">last {days} days</span>
        </div>
        {totals.open_flags > 0 && (
          <button
            onClick={() => navigate('/admin/attribution-review')}
            className="text-xs font-semibold text-amber-300 hover:text-amber-200 inline-flex items-center gap-1"
            data-testid="attribution-insights-review-cta"
          >
            Review {totals.open_flags} open flag{totals.open_flags === 1 ? '' : 's'}
            <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {!anyActivity ? (
        <p className="text-sm text-white/50 py-6 text-center" data-testid="attribution-insights-empty">
          No speaker-vs-extension mismatches detected in this window. All good.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-4">
            <Stat label="Flagged" value={totals.flagged} tone="amber" />
            <Stat label="Auto-reassigned" value={totals.auto_reassigned} tone="teal" />
            <Stat label="Manual reassign" value={totals.manual_reassigned} tone="sky" />
            <Stat label="Open flags" value={totals.open_flags} tone="rose" />
          </div>

          <div className="bg-white/[0.02] rounded-lg p-3">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={weekly_series} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip content={<TOOLTIP />} />
                <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} iconSize={8} />
                <Bar dataKey="flagged" name="Flagged" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                <Bar dataKey="auto_reassigned" name="Auto" fill="#14b8a6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="manual_reassigned" name="Manual" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {top_extensions.length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2 font-semibold">
                Top mismatched extensions
              </p>
              <div className="space-y-1">
                {top_extensions.slice(0, 5).map((row) => (
                  <div
                    key={row.ext}
                    className="flex items-center justify-between bg-white/[0.03] rounded px-3 py-1.5"
                    data-testid={`attribution-insights-ext-${row.ext}`}
                  >
                    <span className="text-xs text-white/80 font-mono">ext {row.ext}</span>
                    <span className="text-xs text-white/50">
                      {row.detected_names.slice(0, 2).join(', ') || 'unknown speaker'}
                      {row.detected_names.length > 2 && ` +${row.detected_names.length - 2}`}
                    </span>
                    <span className="text-xs text-amber-300 font-semibold tabular-nums">
                      {row.count} flag{row.count === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TONE_MAP = {
  amber: 'text-amber-300',
  teal: 'text-teal-300',
  sky: 'text-sky-300',
  rose: 'text-rose-300',
};

const Stat = ({ label, value, tone }) => (
  <div className="bg-white/[0.03] rounded-lg px-3 py-2" data-testid={`attribution-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{label}</p>
    <p className={`text-lg font-bold tabular-nums ${TONE_MAP[tone] || 'text-white'}`}>{value ?? 0}</p>
  </div>
);

export default AttributionInsightsCard;
